<template>
	<div v-if="loading" class="blog-post-state">Loading...</div>

	<div v-else-if="error" class="blog-post-state blog-post-state--error">
		Failed to load this article. Please try again later.
	</div>

	<template v-else-if="blog">
		<div class="blog-post-cover">
			<img :src="blog.coverImage || bannerDefault" :alt="blog.title" />
		</div>

		<div class="blog-post-outer-wrapper">
			<div class="blog-post-inner-wrapper">
				<div class="blog-post-header">
					<div class="blog-post-header__meta">
						<span class="blog-post-header__date">{{ formattedDate }}</span>
						<span v-if="blog.readtime" class="blog-post-header__readtime">
							{{ blog.readtime }} min
						</span>
					</div>
					<h1 class="blog-post-header__title">{{ blog.title }}</h1>
					<p class="blog-post-header__description">{{ blog.description }}</p>
					<span v-if="blog.author" class="blog-post-header__author">
						By {{ blog.author }}
					</span>
				</div>

				<!-- eslint-disable-next-line vue/no-v-html -->
				<div class="blog-post-content" v-html="postHtml" />

				<div v-if="nextBlog" class="blog-post-next">
					<span class="blog-post-next__label">Next article</span>
					<router-link
						:to="{ name: 'BlogPost', params: { link: nextBlog.link } }"
						class="blog-post-next__link"
					>
						{{ nextBlog.title }} &#8594;
					</router-link>
				</div>
			</div>
		</div>
	</template>
</template>

<script setup>
import { useHead } from "@unhead/vue";
import dayjs from "dayjs";
import api from "@/api/index.js";
import bannerDefault from "@/assets/images/blog/banner-default.png";

const route = useRoute();

const blog = ref(null);
const nextBlog = ref(null);
const loading = ref(false);
const error = ref(false);

const formattedDate = computed(() =>
	blog.value ? dayjs(blog.value.createdDate).format("MMM D, YYYY") : "",
);

const postHtml = computed(() => blog.value?.html ?? "");

async function findNextBlog(currentCreatedDate) {
	try {
		const res = await api.getBlogs();
		const all = (res.data.items ?? res.data)
			.slice()
			.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
		const idx = all.findIndex(
			(b) =>
				new Date(b.createdDate).getTime() ===
				new Date(currentCreatedDate).getTime(),
		);
		return idx !== -1 && idx < all.length - 1 ? all[idx + 1] : null;
	} catch {
		return null;
	}
}

async function load(link) {
	loading.value = true;
	error.value = false;
	blog.value = null;
	nextBlog.value = null;

	try {
		const res = await api.getBlog(link);
		blog.value = res.data;

		useHead({
			title: `${res.data.title} - ENON Solutions`,
			meta: [
				{ name: "description", content: res.data.description },
				{ property: "og:title", content: `${res.data.title} - ENON Solutions` },
				{ property: "og:description", content: res.data.description },
				{ property: "og:image", content: res.data.coverImage },
				{ property: "og:type", content: "article" },
			],
		});

		nextBlog.value = await findNextBlog(res.data.createdDate);
	} catch {
		error.value = true;
	} finally {
		loading.value = false;
	}
}

watch(
	() => route.params.link,
	(link) => load(link),
);
onMounted(() => load(route.params.link));
</script>

<style lang="scss" scoped>
.blog-post-state {
	text-align: center;
	font-size: 16px;
	color: #5a6a78;
	padding: 120px 20px;

	&--error {
		color: #c0392b;
	}
}

.blog-post-cover {
	width: 100%;
	max-height: 480px;
	margin-top: $top-page-nav-height;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
}

.blog-post-outer-wrapper {
	padding: 60px 20px 100px;
	background: #fff;
}

.blog-post-inner-wrapper {
	max-width: 780px;
	margin: 0 auto;
}

.blog-post-header {
	margin-bottom: 48px;

	&__meta {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 16px;
	}

	&__date {
		font-size: 14px;
		color: #7a8a99;
	}

	&__readtime {
		font-size: 14px;
		color: $app-color;
		font-weight: 600;

		&::before {
			content: "·";
			margin-right: 12px;
			color: #7a8a99;
		}
	}

	&__title {
		font-size: 42px;
		font-weight: 700;
		color: #142c3a;
		line-height: 1.2;
		margin: 0 0 16px;
	}

	&__description {
		font-size: 18px;
		color: #5a6a78;
		line-height: 1.6;
		margin: 0 0 20px;
	}

	&__author {
		font-size: 14px;
		font-weight: 600;
		color: #142c3a;
	}
}

.blog-post-content {
	:deep(h1),
	:deep(h2),
	:deep(h3),
	:deep(h4) {
		color: #142c3a;
		font-weight: 700;
		line-height: 1.3;
		margin: 2em 0 0.6em;
	}

	:deep(h1) {
		font-size: 32px;
	}
	:deep(h2) {
		font-size: 26px;
	}
	:deep(h3) {
		font-size: 21px;
	}
	:deep(h4) {
		font-size: 18px;
	}

	:deep(p) {
		font-size: 17px;
		line-height: 1.75;
		color: #2e3f4f;
		margin: 0 0 1.4em;
	}

	:deep(a) {
		color: $app-color;
		text-decoration: underline;
	}

	:deep(ul),
	:deep(ol) {
		padding-left: 1.6em;
		margin: 0 0 1.4em;

		li {
			font-size: 17px;
			line-height: 1.75;
			color: #2e3f4f;
			margin-bottom: 0.4em;
		}
	}

	:deep(blockquote) {
		border-left: 4px solid $app-color;
		margin: 0 0 1.4em;
		padding: 12px 20px;
		background: $section-back;
		border-radius: 0 8px 8px 0;

		p {
			margin: 0;
			font-style: italic;
		}
	}

	:deep(pre) {
		background: #1a2633;
		border-radius: 8px;
		padding: 20px;
		overflow-x: auto;
		margin: 0 0 1.4em;

		code {
			font-size: 14px;
			color: #e8f0f7;
			background: none;
			padding: 0;
		}
	}

	:deep(code) {
		font-size: 14px;
		background: $section-back;
		color: #142c3a;
		padding: 2px 6px;
		border-radius: 4px;
	}

	:deep(img) {
		max-width: 100%;
		border-radius: 8px;
		margin: 8px 0 1.4em;
	}

	:deep(hr) {
		border: none;
		border-top: 1px solid #dde3ec;
		margin: 2.5em 0;
	}
}

.blog-post-next {
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 72px;
	padding-top: 32px;
	border-top: 1px solid #dde3ec;

	&__label {
		font-size: 13px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #7a8a99;
	}

	&__link {
		font-size: 18px;
		font-weight: 700;
		color: #142c3a;
		text-decoration: none;
		transition: color 0.15s;

		&:hover {
			color: $app-color;
		}
	}
}

@media screen and (width < 768px) {
	.blog-post-header__title {
		font-size: 28px;
	}

	.blog-post-header__description {
		font-size: 16px;
	}

	.blog-post-content {
		:deep(h1) {
			font-size: 24px;
		}
		:deep(h2) {
			font-size: 21px;
		}
		:deep(h3) {
			font-size: 18px;
		}
		:deep(p),
		:deep(li) {
			font-size: 16px;
		}
	}
}
</style>
